/**
 * @module Services/Bff/Presenters/Response/Cart
 * @desc Cart Presenter
 *
 */

import type { Cart } from 'core/domains';
import { money, formatMoney, upperCaseToLowerCase } from 'core/utilities';

import type { CartResponse } from '../../@types';
import { hasPayload } from '../request/utilities';

function availableItemsPresenter({ availableItems = [] }: CartResponse['data'], payload?: any) {
  return availableItems.map(
    (
      {
        categories,
        currentCycle,
        cycles,
        description,
        images,
        inventory,
        name,
        number,
        origin,
        productType,
        quantity,
        sku,
      },
      index,
    ) => {
      const {
        resellerPriceSpecification: { price },
        priceSpecification: { discount = 0 },
      } = currentCycle.length ? currentCycle[0] : cycles[0];
      const { value: priceTotalNum } = money(price).multiply(quantity);

      return {
        ...(payload?.productCode === sku && { itemListName: payload?.itemListName }),
        department: categories?.map((department) => department.name).join(', '),
        description: upperCaseToLowerCase(description),
        discount,
        image: images.find(({ imageSize }) => imageSize === 'medium') || {},
        inventory: inventory?.managed?.qtd,
        isAvailable: inventory?.managed?.available,
        isCustomCombo: productType === 'CUSTOM_KIT',
        name: upperCaseToLowerCase(name),
        previousQuantity: quantity,
        price: formatMoney(price),
        priceNum: price,
        priceTotal: formatMoney(priceTotalNum),
        priceTotalNum,
        productCode: sku.toString(),
        productPosition: number,
        productIndex: index + 1,
        productOrigin: origin,
        productType,
        quantity,
      };
    },
  );
}

function paymentPlanPresenter(paymentPlan: CartResponse['data']['paymentPlan']) {
  if (Object.keys(paymentPlan).length) {
    const { administrateTax, code, paymentModeId, ...rest } = paymentPlan;

    return {
      administrateTax,
      administrateTaxNum: formatMoney(administrateTax || 0),
      paymentCode: code?.toString(),
      paymentMode: paymentModeId?.toString(),
      paymentPlanType: paymentPlan,
      ...rest,
    };
  }

  return {};
}

function couponPresenter(coupon: CartResponse['data']['coupon']) {
  return {
    ...coupon,
    isValidCoupon: coupon?.couponIsApplied ? coupon?.couponIsApplied : null,
  };
}

function pointsProgramsAvailableItemsPresenter(
  availableItems: CartResponse['data']['pointsPrograms'][number]['availableItems'],
) {
  return availableItems.map(({ description, images, name, number, origin, quantity, sku }) => ({
    description: upperCaseToLowerCase(description),
    image: images?.find(({ imageSize }) => imageSize === 'medium') || {},
    name: upperCaseToLowerCase(name),
    productPosition: number,
    productOrigin: origin,
    quantity,
    productCode: sku.toString(),
  }));
}

function pointsProgramsPresenter(pointsPrograms: CartResponse['data']['pointsPrograms']) {
  return pointsPrograms.map(({ availableItems, code, name, orderUsedPoints, remainingPoints }) => ({
    availableItems: pointsProgramsAvailableItemsPresenter(availableItems),
    code,
    name: upperCaseToLowerCase(name),
    orderUsedPoints,
    remainingPoints,
  }));
}

function getUnavailableItems({ unavailableItems }: CartResponse['data']) {
  return unavailableItems.map((item) => ({
    sku: `${item.sku}`,
    name: item.name,
    quantity: item.quantity,
    image: item.images,
  }));
}

/**
 * Transforms the cart response data model into the cart domain model
 *
 */
export function cartPresenter({ data, success }: CartResponse, payload?: any): Cart['data'] {
  if (!data) {
    return {} as Cart['data'];
  }

  const {
    availableItems,
    coupon,
    id,
    minimumOrder,
    paymentPlan = {},
    pointsPrograms = [],
    price,
  } = data;

  const { minValue, pendingValue, percent: minOrderPercentReached } = minimumOrder;
  const totalUnitCount = availableItems.reduce((acc, item) => acc + item.quantity, 0);

  if (!hasPayload(data)) {
    throw new Error();
  }

  return {
    success,
    code: id,
    coupon: couponPresenter(coupon),
    cashback: `${formatMoney(price?.cashback || 0)}`,
    cashbackNum: price?.cashback || 0,
    items: availableItemsPresenter(data, payload),
    unavailableItems: getUnavailableItems(data),
    minOrderPercentReached: `${minOrderPercentReached}%`,
    minOrderPercentReachedNum: minOrderPercentReached,
    minOrderValue: formatMoney(pendingValue),
    minOrderValueNum: minValue,
    minOrderValuePending: formatMoney(pendingValue),
    minOrderValuePendingNum: pendingValue,
    paymentDetail: paymentPlanPresenter(paymentPlan),
    pointsPrograms: pointsProgramsPresenter(pointsPrograms),
    shipping: price?.shipping ? formatMoney(price?.shipping || 0) : 'Grátis',
    shippingNum: price?.shipping || 0,
    subTotal: formatMoney(price?.products || 0),
    subTotalNum: price?.products || 0,
    total: formatMoney(price?.total || 0),
    totalDiscount: formatMoney(price?.discounts || 0),
    totalDiscountNum: price?.discounts || 0,
    totalItems: availableItems.length,
    totalNum: price?.total || 0,
    totalProfit: formatMoney(price?.commissionValue || 0),
    totalProfitNum: price?.commissionValue || 0,
    totalUnitCount,
  };
}
